import { useTranslation } from "react-i18next"
import * as React from "react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@nexus/ui"
import { ImagePreviewTrigger } from "@/features/identity-hub/components/dashboard/ImagePreviewTrigger"
import { PersonaPortrait } from "../PersonaPortrait/PersonaPortrait"
import {
  formatPersonaAdCount,
  formatPersonaCreatedAt,
  formatPersonaCreatedAtExact,
  formatPersonaSpendExact,
  formatPersonaSpendParts,
  humanizePersonaTitle,
  shareOfSpendPercent,
} from "../../lib/persona-format"
import { PersonaStatusBadge } from "../PersonaStatusBadge/PersonaStatusBadge"
import { personaTypeLabel } from "../../lib/persona-type"
import {
  PERSONA_ENTRANCE_STAGGER_CAP,
  PERSONA_ENTRANCE_STAGGER_MS,
} from "../../constants/personas.constants"
import type {
  Persona,
  PersonaSortDirection,
  PersonaTableSortKey,
} from "../../types/personas.types"

/** Keep in sync with row `motion-safe:duration-*` and OpportunitiesTable row entrance. */
const ROW_ENTRANCE_DURATION_MS = 200

export interface PersonaTableProps {
  personas: Persona[]
  /** Denominator for Spend % — sum of non-REJECTED spend in the current library view. */
  librarySpendTotal: number
  sortKey: PersonaTableSortKey
  sortDir: PersonaSortDirection
  onSort: (key: PersonaTableSortKey) => void
  onPersonaClick: (personaId: string) => void
  /** Omit to drop the action from every row menu (e.g. no set-generate permission). */
  onCreateOpportunitySet?: (persona: Persona) => void
  className?: string
}

function PersonaTableSortHeader({
  label,
  column,
  sortKey,
  sortDir,
  onSort,
  className,
  align = "start",
}: {
  label: string
  column: PersonaTableSortKey
  sortKey: PersonaTableSortKey
  sortDir: PersonaSortDirection
  onSort: (key: PersonaTableSortKey) => void
  className?: string
  align?: "start" | "end"
}) {
  const isActive = sortKey === column
  const iconName = isActive
    ? sortDir === "asc"
      ? "chevron-up"
      : "chevron-down"
    : "chevrons-up-down"

  return (
    <TableHead
      aria-sort={isActive ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
      className={className}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          "group -mx-1 inline-flex items-center gap-1 rounded-sm px-1 py-0.5 transition-colors duration-150 ease-out outline-none hover:text-foreground focus-visible:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40",
          isActive && "text-foreground",
          // Numeric columns lead with the chevron so the label's own right edge — not the
          // icon's — lands on the digits below it. Same convention as OpportunitiesTable.
          align === "end" && "ml-auto flex-row-reverse",
        )}
      >
        {label}
        <Icon
          name={iconName}
          size="xs"
          aria-hidden
          className={cn(
            "shrink-0 transition-opacity duration-150 ease-out",
            isActive
              ? "text-primary opacity-100"
              : "text-muted-foreground/50 opacity-0 group-hover:opacity-60",
          )}
        />
      </button>
    </TableHead>
  )
}

/**
 * The portrait is the persona's identity, so it gets a real 36px face (matching
 * DESIGN.md's `drawer-header` entity icon) rather than a 24px bullet. Clicking it
 * opens the lightbox instead of the row's detail drawer — `ImagePreviewTrigger`
 * stops propagation, and the row's key handler ignores bubbled events.
 */
function PersonaAvatar({ persona, title }: { persona: Persona; title: string }) {
  return (
    <ImagePreviewTrigger src={persona.avatarUrl} alt={title} className="group/avatar rounded-full">
      {/* p-1 pads the 36px face out to a 44px hit area; it lives here rather than on the
          trigger so rows without a portrait (no trigger rendered) still align.
          The lift scales this wrapper rather than the Avatar so the zoom overlay below —
          a sibling pinned to inset-1 — grows with the face instead of exposing an
          undimmed rim. Transform only, so no neighbouring cell reflows. */}
      <span className="relative block p-1 transition-transform duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-safe:group-hover/persona-row:scale-110 motion-safe:group-hover/avatar:scale-125 motion-reduce:transition-none">
        <PersonaPortrait
          avatarUrl={persona.avatarUrl}
          personalityKey={persona.personalityKey}
          title={title}
          fallbackClassName="text-[11px]"
          className="size-9 ring-1 ring-border transition-shadow duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/persona-row:ring-primary/30 group-hover/avatar:ring-2 group-hover/avatar:ring-primary/50 motion-reduce:transition-none"
        />
        {persona.avatarUrl && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-1 flex items-center justify-center rounded-full bg-foreground/50 opacity-0 transition-opacity duration-200 ease-in-out group-hover/avatar:opacity-100 motion-reduce:transition-none"
          >
            <Icon name="maximize-2" size="xs" className="text-background" />
          </span>
        )}
      </span>
    </ImagePreviewTrigger>
  )
}

/** 44px hit area per the accessibility contract, tightened where a fine pointer is precise
 * enough that a full-size target would crowd the row. */
const ROW_ACTIONS_TRIGGER_SIZE_CLASS = "size-11 [@media(hover:hover)_and_(pointer:fine)]:size-7"

/**
 * Row actions sit behind a menu rather than inline buttons: a full table of visible
 * per-row buttons competes with the data for attention, and the row already carries
 * the primary action. Returns null when the row has nothing to offer, so the column
 * stays empty rather than showing a menu that opens onto nothing.
 */
function PersonaRowActions({
  persona,
  title,
  onViewDetails,
  onCreateOpportunitySet,
}: {
  persona: Persona
  title: string
  onViewDetails: () => void
  onCreateOpportunitySet?: (persona: Persona) => void
}) {
  const { t } = useTranslation()

  // Rejected personas are read-only in the library: no drawer, no set generation.
  if (persona.status === "REJECTED") return null

  return (
    // The row opens the drawer on click, so the menu's own clicks must not reach it.
    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("personas.table.actionsMenuAria", {
              title,
              defaultValue: `Actions for ${title}`,
            })}
            className={cn(
              "inline-flex cursor-pointer items-center justify-center rounded-sm text-muted-foreground",
              ROW_ACTIONS_TRIGGER_SIZE_CLASS,
              "transition-[opacity,color,background-color] duration-150 ease-out hover:bg-muted hover:text-foreground",
              "outline-none focus-visible:opacity-100 focus-visible:ring-[3px] focus-visible:ring-ring/40",
              // Hover-reveal keeps the column quiet while scanning rows. Keyboard focus and an
              // open menu force it back; touch devices never fire hover, so there it stays put.
              "opacity-0 group-hover/persona-row:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100",
            )}
          >
            <Icon name="more-horizontal" size="sm" color="inherit" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        {/* No fixed width — the items size the menu, so a longer translation cannot clip. */}
        <DropdownMenuContent align="end">
          <DropdownMenuItem className="gap-2" onSelect={onViewDetails}>
            <Icon name="eye" size="xs" color="inherit" aria-hidden />
            {t("personas.table.viewDetails", "View details")}
          </DropdownMenuItem>
          {onCreateOpportunitySet && (
            <DropdownMenuItem className="gap-2" onSelect={() => onCreateOpportunitySet(persona)}>
              <Icon name="plus" size="xs" color="inherit" aria-hidden />
              {t("personas.card.createOpportunitySet", "Create Opportunity Set")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

/** Sub-1% shares still get a visible sliver so "tracked but tiny" reads differently from zero. */
const SPEND_SHARE_MIN_VISIBLE_PERCENT = 4

/** Renders the Spend % share as a bar so the column can be scanned without reading every number. */
function SpendShare({
  percent,
  label,
  delayMs = 0,
}: {
  percent: number
  label: string
  delayMs?: number
}) {
  const fill = percent > 0 ? Math.max(percent, SPEND_SHARE_MIN_VISIBLE_PERCENT) / 100 : 0
  return (
    <div className="flex items-center justify-end gap-2">
      <span aria-hidden className="h-1 w-10 overflow-hidden rounded-full bg-border">
        <span
          className="persona-spend-fill block h-full w-full rounded-full bg-primary transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
          style={{
            transform: `scaleX(${fill})`,
            animationDelay: delayMs > 0 ? `${delayMs}ms` : undefined,
          }}
        />
      </span>
      <span className="w-8 text-right font-semibold tabular-nums text-foreground">{label}</span>
    </div>
  )
}

/** Compact spend: figure in ink, K/M in muted so $3.8 scans before the magnitude. */
function SpendAmount({ spend, locale }: { spend: number; locale: string }) {
  const { value, suffix } = formatPersonaSpendParts(spend)
  return (
    <span
      className="inline-flex items-baseline tabular-nums"
      title={formatPersonaSpendExact(spend, locale)}
    >
      <span className="font-medium text-foreground">{value}</span>
      {suffix ? <span className="text-muted-foreground">{suffix}</span> : null}
    </span>
  )
}

export function PersonaTable({
  personas,
  librarySpendTotal,
  sortKey,
  sortDir,
  onSort,
  onPersonaClick,
  onCreateOpportunitySet,
  className,
}: PersonaTableProps) {
  const { t, i18n } = useTranslation()
  /** Rows animate once per session — filter/sort reshuffles should not replay the stagger. */
  const seenPersonaRowIdsRef = React.useRef(new Set<string>())

  return (
    <div
      className={cn(
        "overflow-x-auto rounded-lg border border-border bg-card motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200 motion-reduce:animate-none",
        className,
      )}
    >
      <Table>
        <TableHeader className="[&_tr]:border-border">
          <TableRow className="group">
            <PersonaTableSortHeader
              label={t("personas.table.colTitle", "Title")}
              column="title"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
            />
            <PersonaTableSortHeader
              label={t("personas.table.colStatus", "Status")}
              column="status"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
            />
            <PersonaTableSortHeader
              label={t("personas.table.colCreated", "Creation Date")}
              column="createdAt"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
            />
            <PersonaTableSortHeader
              label={t("personas.table.colType", "Core/Industry")}
              column="type"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
            />
            <PersonaTableSortHeader
              label={t("personas.table.colSpendPct", "Spend %")}
              column="shareOfSpend"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
              className="text-right"
              align="end"
            />
            <PersonaTableSortHeader
              label={t("personas.table.colSpend", "Spend $")}
              column="spend"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
              className="text-right"
              align="end"
            />
            <PersonaTableSortHeader
              label={t("personas.table.colAds", "# of ads")}
              column="adCount"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
              className="text-right"
              align="end"
            />
            {/* Unsortable and unlabelled on screen — the menu icons carry the meaning. */}
            <TableHead className="w-12">
              <span className="sr-only">{t("personas.table.colActions", "Actions")}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {personas.map((persona, rowIndex) => {
            const isRejected = persona.status === "REJECTED"
            const displayTitle = humanizePersonaTitle(persona.title)
            const sharePct = isRejected
              ? null
              : shareOfSpendPercent(persona.spend, librarySpendTotal)
            const openDetail = () => {
              if (!isRejected) onPersonaClick(persona.id)
            }
            const isFirstEntrance = !seenPersonaRowIdsRef.current.has(persona.id)
            seenPersonaRowIdsRef.current.add(persona.id)

            return (
              <TableRow
                key={persona.id}
                data-testid="persona-table-row"
                data-status={persona.status}
                style={
                  isFirstEntrance
                    ? {
                        animationDuration: `${ROW_ENTRANCE_DURATION_MS}ms`,
                        animationDelay: `${Math.min(rowIndex, PERSONA_ENTRANCE_STAGGER_CAP) * PERSONA_ENTRANCE_STAGGER_MS}ms`,
                      }
                    : undefined
                }
                tabIndex={isRejected ? undefined : 0}
                aria-label={
                  isRejected
                    ? undefined
                    : t("personas.table.openDetailAria", {
                        title: displayTitle,
                        defaultValue: `Open details for ${displayTitle}`,
                      })
                }
                onClick={openDetail}
                onKeyDown={
                  isRejected
                    ? undefined
                    : (e) => {
                        // Let the avatar's preview button keep its own Enter/Space activation.
                        if (e.target !== e.currentTarget) return
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          openDetail()
                        }
                      }
                }
                className={cn(
                  "group/persona-row",
                  isFirstEntrance &&
                    "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:fill-mode-both motion-reduce:animate-none",
                  isRejected
                    ? "opacity-60"
                    : "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                )}
              >
                <TableCell>
                  {/* -ml-1 cancels the avatar's hit-area padding so the face sits on the
                      same rail as the header label instead of 4px inside it. */}
                  <div className="-ml-1 flex items-center gap-2">
                    <PersonaAvatar persona={persona} title={displayTitle} />
                    <span className="inline-block origin-left font-medium text-foreground transition-transform duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-safe:group-hover/persona-row:scale-110 motion-reduce:transition-none">
                      {displayTitle}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <PersonaStatusBadge status={persona.status} />
                </TableCell>
                <TableCell className="tabular-nums text-muted-foreground">
                  <span title={formatPersonaCreatedAtExact(persona.createdAt, i18n.language)}>
                    {formatPersonaCreatedAt(persona.createdAt, i18n.language)}
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {personaTypeLabel(t, persona.type)}
                </TableCell>
                <TableCell className="text-right">
                  {sharePct === null ? (
                    <span className="tabular-nums text-muted-foreground">
                      {t("personas.table.spendPctUnavailable", "—")}
                    </span>
                  ) : (
                    <SpendShare
                      percent={sharePct}
                      delayMs={
                        isFirstEntrance
                          ? Math.min(rowIndex, PERSONA_ENTRANCE_STAGGER_CAP) *
                            PERSONA_ENTRANCE_STAGGER_MS
                          : 0
                      }
                      label={t("personas.table.spendPctValue", {
                        value: Math.round(sharePct),
                        defaultValue: `${Math.round(sharePct)}%`,
                      })}
                    />
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <SpendAmount spend={persona.spend} locale={i18n.language} />
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {formatPersonaAdCount(persona.adCount, i18n.language)}
                </TableCell>
                <TableCell className="text-right">
                  <PersonaRowActions
                    persona={persona}
                    title={displayTitle}
                    onViewDetails={openDetail}
                    onCreateOpportunitySet={onCreateOpportunitySet}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

export function PersonaTableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div
      data-testid="persona-table-skeleton"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="grid h-9 grid-cols-8 items-center gap-2 border-b border-border bg-muted px-4">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-2.5 animate-pulse rounded bg-border" />
        ))}
      </div>
      {/* min-h matches a real row (36px avatar + cell padding) so rows don't jump on load. */}
      {Array.from({ length: rows }).map((_, row) => (
        <div
          key={row}
          className="grid min-h-14 grid-cols-8 items-center gap-2 border-b border-border-subtle px-4 last:border-0"
        >
          <div className="flex items-center gap-3">
            <div className="size-9 shrink-0 animate-pulse rounded-full bg-border" />
            <div className="h-3 flex-1 animate-pulse rounded bg-border" />
          </div>
          {Array.from({ length: 6 }).map((_, col) => (
            <div key={col} className="h-3 animate-pulse rounded bg-border" />
          ))}
        </div>
      ))}
    </div>
  )
}
